import { getWebGLRendererString } from '~/lib/hasHardwareWebGL';

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

const GPU_WEIGHT = 45;
const MEMORY_WEIGHT = 25;
const CORES_WEIGHT = 20;
const MOBILE_WEIGHT = 10;

const GPU_NEUTRAL = 0.4;
const MEMORY_NEUTRAL = 0.5;
const CORES_NEUTRAL = 0.5;

export interface CapabilitySignals {
  rendererString: string | null;
  deviceMemory: number | undefined;
  hardwareConcurrency: number | undefined;
  isMobile: boolean;
}

/**
 * @returns The static capability signals read from the current client, or SSR-safe defaults
 */
export function readCapabilitySignals(): CapabilitySignals {
  const nav: Navigator | undefined = 'undefined' !== typeof navigator ? navigator : undefined;

  return {
    rendererString: getWebGLRendererString(),
    deviceMemory: (nav as (Navigator & { deviceMemory?: number }) | undefined)?.deviceMemory,
    hardwareConcurrency: nav?.hardwareConcurrency,
    isMobile: 'undefined' !== typeof window ? window.matchMedia(MOBILE_MEDIA_QUERY).matches : false,
  };
}

/**
 * @param renderer - UNMASKED_RENDERER_WEBGL string, or null when unavailable
 * @returns True when the string matches a known software rasterizer (SwiftShader, llvmpipe, software)
 */
export function isSoftwareRenderer(renderer: string | null): boolean {
  if (null === renderer) {
    return false;
  }
  return /swiftshader|llvmpipe|software/i.test(renderer);
}

/**
 * @param renderer - UNMASKED_RENDERER_WEBGL string, or null when unavailable
 * @returns A normalised GPU sub-score; discrete 1.0, mid 0.75, integrated 0.4, unknown falls to the neutral default
 */
function normaliseGpu(renderer: string | null): number {
  if (null === renderer) {
    return GPU_NEUTRAL;
  }
  if (/nvidia|geforce|rtx|gtx|radeon|\bamd\b/i.test(renderer)) {
    return 1.0;
  }
  if (/apple\s*[am]\d|adreno\s*[6-9]\d\d|iris\s*xe/i.test(renderer)) {
    return 0.75;
  }
  if (/intel\s*(?:hd|uhd)|\bmali\b|adreno\s*[34]\d\d/i.test(renderer)) {
    return 0.4;
  }
  return GPU_NEUTRAL;
}

/**
 * @param signals - Static capability signals
 * @returns An integer capability score from 0 to 100 inclusive; unknown signals contribute their neutral default
 */
export function computeCapabilityScore(signals: CapabilitySignals): number {
  const gpuNorm = normaliseGpu(signals.rendererString);

  const memoryNorm
    = 'number' === typeof signals.deviceMemory && Number.isFinite(signals.deviceMemory)
      ? clamp01(signals.deviceMemory / 8)
      : MEMORY_NEUTRAL;

  const coresNorm
    = 'number' === typeof signals.hardwareConcurrency && Number.isFinite(signals.hardwareConcurrency)
      ? clamp01(signals.hardwareConcurrency / 8)
      : CORES_NEUTRAL;

  const mobileNorm = signals.isMobile ? 0 : 1;

  const score
    = GPU_WEIGHT * gpuNorm + MEMORY_WEIGHT * memoryNorm + CORES_WEIGHT * coresNorm + MOBILE_WEIGHT * mobileNorm;

  return Math.round(score);
}

/**
 * @param value - Any finite number
 * @returns The value clamped to the inclusive range 0 to 1
 */
function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
